/**
 * LocalOnboardingStore — IN-3.a-lite
 * ──────────────────────────────────
 * Wraps the existing localStorage persistence behind the OnboardingBackend
 * interface. Behavior is identical to the inline persistence that lived
 * in OnboardingProvider (IN-2.h hardened): corrupt payloads are
 * quarantined under `<key>.corrupt.<ts>`, unknown versions fall back to
 * empty, and per-slice validation degrades only the malformed slice.
 */

import { ONBOARDING_STORAGE_KEY } from "../types";
import {
  emptyOnboardingSnapshot,
  ONBOARDING_SNAPSHOT_VERSION,
  type OnboardingBackend,
  type OnboardingSnapshot,
} from "./types";

function quarantine(raw: string, reason: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      `${ONBOARDING_STORAGE_KEY}.corrupt.${Date.now()}`,
      JSON.stringify({ reason, raw }),
    );
  } catch {
    /* noop */
  }
}

export class LocalOnboardingStore implements OnboardingBackend {
  readonly kind = "local" as const;

  loadSnapshot(): OnboardingSnapshot {
    if (typeof window === "undefined") return emptyOnboardingSnapshot();
    let raw: string | null = null;
    try {
      raw = window.localStorage.getItem(ONBOARDING_STORAGE_KEY);
    } catch {
      return emptyOnboardingSnapshot();
    }
    if (!raw) return emptyOnboardingSnapshot();
    let parsed: Partial<OnboardingSnapshot> & { v?: unknown };
    try {
      parsed = JSON.parse(raw);
    } catch {
      quarantine(raw, "json_parse_error");
      return emptyOnboardingSnapshot();
    }
    if (
      parsed &&
      typeof parsed === "object" &&
      parsed.v !== undefined &&
      parsed.v !== ONBOARDING_SNAPSHOT_VERSION
    ) {
      quarantine(raw, `unknown_version:${String(parsed.v)}`);
      return emptyOnboardingSnapshot();
    }
    const parsedSeq = (parsed as { seq?: unknown }).seq;
    return {
      v: ONBOARDING_SNAPSHOT_VERSION,
      seq: typeof parsedSeq === "number" && Number.isFinite(parsedSeq) ? parsedSeq : 0,
      inviteTokens: Array.isArray(parsed.inviteTokens) ? parsed.inviteTokens : [],
      sessions: Array.isArray(parsed.sessions) ? parsed.sessions : [],
      auditEvents: Array.isArray(parsed.auditEvents) ? parsed.auditEvents : [],
      complianceDocuments: Array.isArray(parsed.complianceDocuments)
        ? parsed.complianceDocuments
        : [],
    };
  }

  saveSnapshot(snapshot: OnboardingSnapshot): void {
    if (typeof window === "undefined") return;
    // Sprint B.2.1 — let natural errors (e.g. QuotaExceededError) bubble
    // to the provider so the save-status indicator can render `error`.
    // The provider catches and does not crash the app.
    window.localStorage.setItem(
      ONBOARDING_STORAGE_KEY,
      JSON.stringify(snapshot),
    );
  }
}
